import { useId, useState } from 'react';
import type { Key } from 'react';
import {
  DeleteOutlined,
  EditOutlined,
  ExportOutlined,
  FileTextOutlined,
  FolderAddOutlined,
  FolderOutlined,
  LinkOutlined,
  LockOutlined,
  PaperClipOutlined,
  PlusOutlined,
  SearchOutlined,
  SwapOutlined,
} from '@ant-design/icons';

import { Bento, DataTable, MenuThaoTac, PageHeader, Tile, TileHeader } from '../../../components/shared';
import { Badge, Button, Checkbox, Dialog, Empty, Field, Input, Select, Skeleton } from '../../../components/ui';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { KHONG_CO, ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { loiTheoTruong } from '../../../lib/errors';
import { useToast } from '../../../lib/toast';
import { HopXacNhanThaoTac } from '../../nguoi-dung/components/HopXacNhanThaoTac';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { CayThuMuc } from '../components/CayThuMuc';
import { ChonNguon } from '../components/ChonNguon';
import { ChonThuMuc } from '../components/ChonThuMuc';
import { DaiBao } from '../components/DaiBao';
import { HopTaiLieu } from '../components/HopTaiLieu';
import { DANH_MUC, QUYEN } from '../constants';
import { useChuyenTaiLieu, useDanhSachTaiLieu, useGoTaiLieu, useLoiBaoCao, useMoTaiLieu } from '../hooks/useBaoCao';
import { useNguon } from '../hooks/useNguon';
import {
  CHUA_PHAN_LOAI,
  useChuyenThuMuc,
  useDanhSachThuMuc,
  useDemChuaPhanLoai,
  useSuaThuMuc,
  useTaoThuMuc,
  useThuMucDangXem,
  useXoaThuMuc,
} from '../hooks/useThuMuc';
import { coTep } from '../nghiep-vu/payload';
import { tenDayDu } from '../nghiep-vu/cay-thu-muc';
import { CATEGORIES } from '../types';
import type { Category, DocumentItem, Folder } from '../types';

type Hop =
  | { loai: 'tai-lieu'; goc?: DocumentItem }
  | { loai: 'go'; tl: DocumentItem }
  | { loai: 'chuyen-tai-lieu'; ids: string[] }
  | { loai: 'thu-muc'; thuMuc?: Folder; chaId?: string }
  | { loai: 'chuyen-thu-muc'; thuMuc: Folder }
  | { loai: 'xoa-thu-muc'; thuMuc: Folder }
  | null;

/**
 * Tài liệu báo cáo (`/bao-cao-doanh-nghiep/tai-lieu`, phân hệ `BIZ_REPORT_DOC`): cây thư mục bên trái, bảng
 * tài liệu chọn nhiều dòng bên phải. Thư mục là chỗ cất; **danh mục** mới quyết định phân hệ nào của app
 * hiện tài liệu. `?nguon=` và `?thuMuc=` nằm trên URL.
 */
export function TaiLieuView() {
  const { t, locale } = useT();
  const toast = useToast();
  const dichLoi = useLoiBaoCao();
  const quyen = useModulePermissions(QUYEN);
  const nguon = useNguon();

  const cay = useDanhSachThuMuc(nguon.code, nguon.sanSang && quyen.thuMucDoc);
  const dsThuMuc = cay.data ?? [];
  const { chon: thuMuc, datChon: datThuMuc } = useThuMucDangXem(dsThuMuc);
  const demGoc = useDemChuaPhanLoai(nguon.code, nguon.sanSang && quyen.thuMucDoc);

  const [nhap, setNhap] = useState('');
  const [timKiem, setTimKiem] = useState('');
  const [danhMuc, setDanhMuc] = useState<Category | undefined>();
  const [keCon, setKeCon] = useState(false);
  const [daChon, setDaChon] = useState<string[]>([]);
  const [hop, setHop] = useState<Hop>(null);

  const loc = {
    ...(nguon.code !== undefined ? { source: nguon.code } : {}),
    ...(thuMuc === CHUA_PHAN_LOAI
      ? { chuaPhanLoai: true }
      : thuMuc !== undefined
        ? { folder: thuMuc, ...(keCon ? { deep: true } : {}) }
        : {}),
    ...(danhMuc ? { category: danhMuc } : {}),
    ...(timKiem ? { q: timKiem } : {}),
  };
  // Cây chưa về thì chưa biết `?thuMuc=` có hợp lệ: đợi, để không tải "Tất cả" rồi ngay sau đó tải lại.
  const q = useDanhSachTaiLieu(loc, nguon.sanSang && quyen.taiLieuDoc && !(quyen.thuMucDoc && cay.isPending));

  /*
    Đổi bộ lọc, thư mục hay nguồn thì bỏ chọn — giữ lại là thanh hành động nói "đã chọn 5" trong khi bảng
    không còn hiện cái nào, rồi một cú bấm "Chuyển" tác động lên tài liệu người dùng không nhìn thấy.
    Điều chỉnh ngay trong lần vẽ, không qua effect.
  */
  const khoaLoc = JSON.stringify(loc);
  const [khoaLocCu, setKhoaLocCu] = useState(khoaLoc);
  if (khoaLocCu !== khoaLoc) {
    setKhoaLocCu(khoaLoc);
    if (daChon.length > 0) setDaChon([]);
  }

  const go = useGoTaiLieu();
  const xoaTM = useXoaThuMuc();
  const mo = useMoTaiLieu();

  const ghiDuoc = nguon.ghiDuoc;
  const coLoc = Boolean(timKiem || danhMuc);
  const tieuDe = t('nav.muc.taiLieuBaoCao');
  const thuMucMacDinh = thuMuc !== undefined && thuMuc !== CHUA_PHAN_LOAI ? thuMuc : null;
  const moThem = () => setHop({ loai: 'tai-lieu' });
  const dongHop = () => setHop(null);

  const header = (
    <PageHeader
      breadcrumb={[{ label: t('nav.nhom.baoCaoDoanhNghiep') }, { label: tieuDe }]}
      titleIcon={<FileTextOutlined className="text-warning-600" />}
      title={tieuDe}
      subtitle={t('baoCaoDoanhNghiep.taiLieu.moTa')}
      actions={
        <>
          <ChonNguon nguon={nguon} />
          {quyen.taiLieuDoc && quyen.taiLieuTao && ghiDuoc && (
            <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={moThem}>
              {t('baoCaoDoanhNghiep.taiLieu.them')}
            </Button>
          )}
        </>
      }
    />
  );

  if (!quyen.taiLieuDoc) {
    return (
      <div className="space-y-4 p-4 lg:p-6">
        {header}
        <Tile>
          <Empty
            icon={LockOutlined}
            title={t('baoCaoDoanhNghiep.khongCoQuyen.tieuDe')}
            description={t('baoCaoDoanhNghiep.khongCoQuyen.moTa', { quyen: QUYEN.taiLieuDoc })}
          />
        </Tile>
      </div>
    );
  }

  const loiTaiDau = q.isError && q.dong.length === 0 && !q.isFetchNextPageError;
  const tenThuMucDangXem =
    thuMuc === CHUA_PHAN_LOAI
      ? t('baoCaoDoanhNghiep.thuMuc.chuaPhanLoai')
      : thuMuc !== undefined
        ? tenDayDu(dsThuMuc, thuMuc)
        : t('baoCaoDoanhNghiep.thuMuc.tatCa');

  const rong = coLoc ? (
    <Empty icon={SearchOutlined} title={t('baoCaoDoanhNghiep.taiLieu.rongLoc')} />
  ) : (
    <Empty
      icon={FileTextOutlined}
      title={t(
        thuMuc === CHUA_PHAN_LOAI
          ? 'baoCaoDoanhNghiep.thuMuc.chuaPhanLoaiTrong'
          : thuMuc !== undefined
            ? 'baoCaoDoanhNghiep.thuMuc.trong'
            : 'baoCaoDoanhNghiep.taiLieu.trong',
      )}
      description={t(
        thuMuc === CHUA_PHAN_LOAI
          ? 'baoCaoDoanhNghiep.thuMuc.chuaPhanLoaiTrongMoTa'
          : thuMuc !== undefined
            ? 'baoCaoDoanhNghiep.thuMuc.trongMoTa'
            : 'baoCaoDoanhNghiep.taiLieu.trongMoTa',
      )}
      action={
        quyen.taiLieuTao && ghiDuoc && thuMuc !== CHUA_PHAN_LOAI ? (
          <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={moThem}>
            {t('baoCaoDoanhNghiep.taiLieu.themDauTien')}
          </Button>
        ) : undefined
      }
    />
  );

  return (
    <div className="space-y-4 p-4 lg:p-6">
      {header}

      {nguon.nguon && !nguon.nguon.isActive && (
        <DaiBao tong="canhBao">{t('baoCaoDoanhNghiep.nguonDaNgungBanner', { ten: nguon.nguon.name })}</DaiBao>
      )}

      <Bento>
        {quyen.thuMucDoc && (
          <Tile span="lg:col-span-3" className="h-fit lg:sticky lg:top-4 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
            <CayThuMuc
              danhSach={dsThuMuc}
              dangTai={!nguon.sanSang || cay.isPending}
              loi={cay.isError ? t('baoCaoDoanhNghiep.thuMuc.loiTai', { loi: dichLoi(cay.error) }) : null}
              onTaiLai={() => {
                void cay.refetch();
                void demGoc.refetch();
              }}
              chon={thuMuc}
              onChon={datThuMuc}
              {...(thuMuc === undefined && !coLoc && q.tong !== undefined ? { tongTatCa: q.tong } : {})}
              {...(demGoc.data ? { soChuaPhanLoai: demGoc.data.documentsTotal } : {})}
              cuoiCay={
                quyen.thuMucTao && ghiDuoc ? (
                  <Button variant="ghost" size="sm" icon={<FolderAddOutlined />} onClick={() => setHop({ loai: 'thu-muc' })}>
                    {t('baoCaoDoanhNghiep.thuMuc.them')}
                  </Button>
                ) : undefined
              }
              thaoTac={(f) =>
                ghiDuoc ? (
                  <MenuThaoTac
                    tenBanGhi={f.name}
                    muc={[
                      { khoa: 'con', nhan: t('baoCaoDoanhNghiep.thuMuc.themCon'), icon: <FolderAddOutlined />, hien: quyen.thuMucTao,
                        onChon: () => setHop({ loai: 'thu-muc', chaId: f.id }) },
                      { khoa: 'ten', nhan: t('baoCaoDoanhNghiep.thuMuc.doiTen'), icon: <EditOutlined />, hien: quyen.thuMucSua,
                        onChon: () => setHop({ loai: 'thu-muc', thuMuc: f }) },
                      { khoa: 'chuyen', nhan: t('baoCaoDoanhNghiep.thuMuc.chuyen'), icon: <SwapOutlined />, hien: quyen.thuMucSua,
                        onChon: () => setHop({ loai: 'chuyen-thu-muc', thuMuc: f }) },
                      { khoa: 'xoa', nhan: t('baoCaoDoanhNghiep.thuMuc.xoa'), icon: <DeleteOutlined />, nhom: 'pha-huy',
                        hien: quyen.thuMucXoa, onChon: () => setHop({ loai: 'xoa-thu-muc', thuMuc: f }) },
                    ]}
                  />
                ) : null
              }
            />
          </Tile>
        )}

        <Tile span={quyen.thuMucDoc ? 'lg:col-span-9' : undefined} className="min-w-0">
          <TileHeader
            icon={<FolderOutlined />}
            tone="warning"
            title={tenThuMucDangXem}
            {...(q.tong !== undefined
              ? { mo: t('baoCaoDoanhNghiep.taiLieu.dangHien', { hien: soVN(q.dong.length, locale), tong: soVN(q.tong, locale) }) }
              : {})}
          />

          {/* Bộ lọc là một hàng riêng: khe `action` của tiêu đề ô `shrink-0`, hai ô nhập đặt vào đó sẽ đè tiêu đề khi hẹp. */}
          <div className="flex flex-wrap items-center gap-2.5 border-b border-default px-5 py-3">
            <Input
              allowClear
              className="w-full sm:w-72"
              prefix={<SearchOutlined className="text-content-muted" />}
              placeholder={t('baoCaoDoanhNghiep.taiLieu.tim')}
              aria-label={t('baoCaoDoanhNghiep.taiLieu.tim')}
              value={nhap}
              onChange={(e) => {
                setNhap(e.target.value);
                if (!e.target.value) setTimKiem('');
              }}
              onPressEnter={() => setTimKiem(nhap.trim())}
              onBlur={() => setTimKiem(nhap.trim())}
            />
            <Select<Category>
              allowClear
              className="w-full sm:w-48"
              placeholder={t('baoCaoDoanhNghiep.taiLieu.tatCaDanhMuc')}
              aria-label={t('baoCaoDoanhNghiep.taiLieu.locDanhMuc')}
              value={danhMuc}
              onChange={(v) => setDanhMuc(v)}
              options={CATEGORIES.map((c) => ({ value: c, label: t(DANH_MUC[c].khoa) }))}
            />
            {/* Chỉ có nghĩa khi đứng trong một thư mục: "Tất cả" đã hiện hết, "Chưa phân loại" không có con. */}
            {thuMuc !== undefined && thuMuc !== CHUA_PHAN_LOAI && (
              <Checkbox checked={keCon} onChange={(e) => setKeCon(e.target.checked)}>
                {t('baoCaoDoanhNghiep.thuMuc.keCaThuMucCon')}
              </Checkbox>
            )}
          </div>

          {/* Thanh hành động hàng loạt ngay trên bảng: đặt dưới thì nằm ngoài màn hình đúng lúc vừa chọn xong. */}
          {daChon.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 border-b border-default bg-subtle px-5 py-2.5">
              <span className="text-sm text-content-secondary">
                {t('baoCaoDoanhNghiep.taiLieu.daChon', { so: soVN(daChon.length, locale) })}
              </span>
              <Button
                variant="outline"
                size="sm"
                icon={<SwapOutlined />}
                onClick={() => setHop({ loai: 'chuyen-tai-lieu', ids: daChon })}
              >
                {t('baoCaoDoanhNghiep.taiLieu.chuyenSo', { so: soVN(daChon.length, locale) })}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setDaChon([])}>
                {t('baoCaoDoanhNghiep.taiLieu.boChon')}
              </Button>
            </div>
          )}

          {loiTaiDau ? (
            <div className="p-4">
              <DaiBao
                tong="loi"
                hanhDong={
                  <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
                    {t('chung.thuLai')}
                  </Button>
                }
              >
                {t('baoCaoDoanhNghiep.taiLieu.loiTai', { loi: dichLoi(q.error) })}
              </DaiBao>
            </div>
          ) : q.isPending ? (
            <div className="space-y-2 p-5" aria-busy="true">
              {Array.from({ length: 6 }, (_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : q.dong.length === 0 ? (
            rong
          ) : (
            <>
              <DataTable<DocumentItem>
                caption={t('baoCaoDoanhNghiep.taiLieu.caption', { noi: tenThuMucDangXem })}
                dataSource={q.dong}
                pagination={false}
                rowKey={(d) => d.id}
                {...(quyen.taiLieuSua && ghiDuoc && quyen.thuMucDoc
                  ? { rowSelection: { selectedRowKeys: daChon, onChange: (keys: Key[]) => setDaChon(keys.map(String)) } }
                  : {})}
                columns={[
                  {
                    title: t('baoCaoDoanhNghiep.taiLieu.cotTieuDe'),
                    key: 'title',
                    render: (_, d) => <OTieuDe d={d} />,
                  },
                  {
                    title: t('baoCaoDoanhNghiep.taiLieu.cotDanhMuc'),
                    key: 'category',
                    width: 130,
                    render: (_, d) => <Badge variant={DANH_MUC[d.category].variant}>{t(DANH_MUC[d.category].khoa)}</Badge>,
                  },
                  {
                    title: t('baoCaoDoanhNghiep.taiLieu.cotLoai'),
                    key: 'type',
                    width: 110,
                    render: (_, d) => (
                      <span className="whitespace-nowrap text-xs text-content-secondary">{t(`baoCaoDoanhNghiep.loaiTep.${d.type}`)}</span>
                    ),
                  },
                  {
                    title: t('baoCaoDoanhNghiep.taiLieu.cotCapNhat'),
                    key: 'updatedAt',
                    width: 150,
                    render: (_, d) => <span className="whitespace-nowrap text-xs tabular-nums">{ngayGioVN(d.updatedAt, locale)}</span>,
                  },
                  {
                    title: <span className="sr-only">{t('chung.thaoTac')}</span>,
                    key: 'thaoTac',
                    width: 72,
                    align: 'center',
                    render: (_, d) => (
                      <MenuThaoTac
                        tenBanGhi={d.title}
                        muc={[
                          { khoa: 'mo', nhan: t(coTep(d) ? 'baoCaoDoanhNghiep.taiLieu.moTep' : 'baoCaoDoanhNghiep.taiLieu.moLienKet'),
                            icon: <ExportOutlined />, nhom: 'xem',
                            onChon: () => mo.mutate(d.id, { onError: (e) => toast.error(dichLoi(e)) }) },
                          { khoa: 'sua', nhan: t('baoCaoDoanhNghiep.taiLieu.sua'), icon: <EditOutlined />, hien: quyen.taiLieuSua && ghiDuoc,
                            onChon: () => setHop({ loai: 'tai-lieu', goc: d }) },
                          { khoa: 'chuyen', nhan: t('baoCaoDoanhNghiep.taiLieu.chuyenThuMuc'), icon: <SwapOutlined />,
                            hien: quyen.taiLieuSua && ghiDuoc && quyen.thuMucDoc,
                            onChon: () => setHop({ loai: 'chuyen-tai-lieu', ids: [d.id] }) },
                          { khoa: 'go', nhan: t('baoCaoDoanhNghiep.taiLieu.go'), icon: <DeleteOutlined />, nhom: 'pha-huy',
                            hien: quyen.taiLieuXoa && ghiDuoc, onChon: () => setHop({ loai: 'go', tl: d }) },
                        ]}
                      />
                    ),
                  },
                ]}
              />
              {(q.hasNextPage || q.isFetchNextPageError) && (
                <div className="flex flex-wrap items-center justify-end gap-3 border-t border-default px-5 py-3">
                  {q.isFetchNextPageError && (
                    <span role="alert" className="min-w-0 flex-1 text-xs text-danger-700">
                      {t('baoCaoDoanhNghiep.taiLieu.loiTaiThem', { loi: dichLoi(q.error) })}
                    </span>
                  )}
                  <Button variant="outline" size="sm" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
                    {q.isFetchNextPageError ? t('chung.thuLai') : t('chung.taiThem')}
                  </Button>
                </div>
              )}
            </>
          )}
        </Tile>
      </Bento>

      {hop?.loai === 'tai-lieu' && (
        <HopTaiLieu
          key={hop.goc?.id ?? 'moi'}
          {...(hop.goc ? { goc: hop.goc } : {})}
          thuMucMacDinh={thuMucMacDinh}
          dsThuMuc={dsThuMuc}
          nguon={nguon.code}
          onClose={dongHop}
        />
      )}

      {hop?.loai === 'go' && (
        <HopXacNhanThaoTac
          open
          tieuDe={t('baoCaoDoanhNghiep.taiLieu.goTieuDe', { ten: hop.tl.title })}
          than={t('baoCaoDoanhNghiep.taiLieu.goThan')}
          nhanXacNhan={t('baoCaoDoanhNghiep.taiLieu.go')}
          nguyHiem
          dangChay={go.isPending}
          loi={go.error ? dichLoi(go.error) : null}
          onClose={() => {
            go.reset();
            dongHop();
          }}
          onXacNhan={() =>
            go.mutate(hop.tl.id, {
              onSuccess: () => {
                toast.success(t('baoCaoDoanhNghiep.taiLieu.daGo', { ten: hop.tl.title }));
                setDaChon((cu) => cu.filter((id) => id !== hop.tl.id));
                dongHop();
              },
            })
          }
        />
      )}

      {hop?.loai === 'chuyen-tai-lieu' && (
        <HopChuyenTaiLieu
          ids={hop.ids}
          dsThuMuc={dsThuMuc}
          onClose={dongHop}
          onXong={() => {
            setDaChon([]);
            dongHop();
          }}
        />
      )}

      {hop?.loai === 'thu-muc' && (
        <HopThuMuc
          {...(hop.thuMuc ? { thuMuc: hop.thuMuc } : {})}
          {...(hop.chaId ? { chaId: hop.chaId, tenCha: tenDayDu(dsThuMuc, hop.chaId) } : {})}
          nguon={nguon.code}
          onClose={dongHop}
        />
      )}

      {hop?.loai === 'chuyen-thu-muc' && <HopChuyenThuMuc thuMuc={hop.thuMuc} dsThuMuc={dsThuMuc} onClose={dongHop} />}

      {hop?.loai === 'xoa-thu-muc' && (
        <HopXacNhanThaoTac
          open
          tieuDe={t('baoCaoDoanhNghiep.thuMuc.xoaTieuDe', { ten: hop.thuMuc.name })}
          than={t('baoCaoDoanhNghiep.thuMuc.xoaThan')}
          nhanXacNhan={t('baoCaoDoanhNghiep.thuMuc.xoa')}
          nguyHiem
          dangChay={xoaTM.isPending}
          loi={xoaTM.error ? dichLoi(xoaTM.error) : null}
          onClose={() => {
            xoaTM.reset();
            dongHop();
          }}
          onXacNhan={() =>
            xoaTM.mutate(hop.thuMuc.id, {
              onSuccess: () => {
                toast.success(t('baoCaoDoanhNghiep.thuMuc.daXoa', { ten: hop.thuMuc.name }));
                // Đang đứng trong thư mục vừa xoá thì lùi về "Tất cả".
                if (thuMuc === hop.thuMuc.id) datThuMuc(undefined);
                dongHop();
              },
            })
          }
        />
      )}
    </div>
  );
}

/** Ô tiêu đề: tên tài liệu, rồi tên tệp (tệp tải lên) hoặc tên miền của liên kết, rồi tóm tắt. */
function OTieuDe({ d }: { d: DocumentItem }) {
  let phu = d.fileName;
  if (!phu) {
    try {
      phu = new URL(d.url).host;
    } catch {
      phu = d.url;
    }
  }
  return (
    <div className="flex min-w-0 max-w-md flex-col gap-0.5">
      <span className="line-clamp-2 text-sm font-medium text-content-primary">{d.title}</span>
      <span className="flex min-w-0 items-center gap-1 text-2xs text-content-muted">
        {coTep(d) ? <PaperClipOutlined aria-hidden /> : <LinkOutlined aria-hidden />}
        <span className="truncate" title={d.fileName ?? d.url}>
          {phu || KHONG_CO}
        </span>
      </span>
      {d.summary && <span className="line-clamp-1 text-xs text-content-secondary">{d.summary}</span>}
    </div>
  );
}

/** Chuyển một hay nhiều tài liệu sang thư mục khác (tuần tự, báo số đã chuyển khi dừng giữa chừng). */
function HopChuyenTaiLieu({
  ids,
  dsThuMuc,
  onClose,
  onXong,
}: {
  ids: string[];
  dsThuMuc: readonly Folder[];
  onClose: () => void;
  onXong: () => void;
}) {
  const { t, locale } = useT();
  const toast = useToast();
  const dichLoi = useLoiBaoCao();
  const chuyen = useChuyenTaiLieu();
  const [dich, setDich] = useState<string | null>(null);
  const so = soVN(ids.length, locale);

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('baoCaoDoanhNghiep.taiLieu.chuyenTieuDe', { so })}
      width={480}
      destroyOnHidden
      footer={
        <div className="space-y-3">
          {chuyen.error && (
            <LoiTrongHop>
              {t('baoCaoDoanhNghiep.taiLieu.chuyenDoDang', {
                xong: soVN(chuyen.error.xong, locale),
                tong: so,
                loi: dichLoi(chuyen.error.loi),
              })}
            </LoiTrongHop>
          )}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button
              variant="primary"
              size="md"
              loading={chuyen.isPending}
              onClick={() =>
                chuyen.mutate(
                  { ids, folderId: dich },
                  {
                    onSuccess: (n) => {
                      toast.success(t('baoCaoDoanhNghiep.taiLieu.daChuyen', { so: soVN(n, locale) }));
                      onXong();
                    },
                  },
                )
              }
            >
              {t('baoCaoDoanhNghiep.taiLieu.chuyenSo', { so })}
            </Button>
          </div>
        </div>
      }
    >
      <div className="pt-2">
        <Field label={t('baoCaoDoanhNghiep.taiLieu.chuyenToi')}>
          {(p) => (
            <ChonThuMuc
              {...p}
              danhSach={dsThuMuc}
              giaTri={dich}
              onChon={(id) => {
                setDich(id);
                chuyen.reset();
              }}
              nhanGoc={t('baoCaoDoanhNghiep.thuMuc.chuaPhanLoai')}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

/** Tạo thư mục (ở gốc hoặc trong `chaId`) hay đổi tên `thuMuc`. Trùng tên trong cùng cha: lỗi dưới ô tên. */
function HopThuMuc({
  thuMuc,
  chaId,
  tenCha,
  nguon,
  onClose,
}: {
  thuMuc?: Folder;
  chaId?: string;
  tenCha?: string;
  nguon: string | undefined;
  onClose: () => void;
}) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoiBaoCao();
  const formId = useId();
  const tao = useTaoThuMuc();
  const sua = useSuaThuMuc();
  const dang = thuMuc ? sua : tao;
  const [ten, setTen] = useState(thuMuc?.name ?? '');
  const [daGui, setDaGui] = useState(false);

  const loiTrong = daGui && ten.trim() === '' ? t('baoCaoDoanhNghiep.loi.batBuoc') : undefined;
  const loiTen = loiTrong ?? loiTheoTruong(dang.error)['/name'] ?? (dang.error?.code === 'BAO_CAO_FOLDER_NAME_EXISTS' ? dang.error.message || undefined : undefined);
  const loiChung = dang.error && !loiTen ? dichLoi(dang.error) : null;

  const gui = () => {
    setDaGui(true);
    const ten2 = ten.trim();
    if (!ten2 || dang.isPending) return;
    const xong = (f: Folder) => {
      toast.success(t(thuMuc ? 'baoCaoDoanhNghiep.thuMuc.daDoiTen' : 'baoCaoDoanhNghiep.thuMuc.daTao', { ten: f.name }));
      onClose();
    };
    if (thuMuc) {
      if (ten2 === thuMuc.name) {
        onClose();
        return;
      }
      sua.mutate({ id: thuMuc.id, du: { name: ten2 } }, { onSuccess: xong });
    } else {
      tao.mutate(
        { name: ten2, ...(nguon !== undefined ? { source: nguon } : {}), ...(chaId ? { parentId: chaId } : {}) },
        { onSuccess: xong },
      );
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t(thuMuc ? 'baoCaoDoanhNghiep.thuMuc.doiTenTieuDe' : chaId ? 'baoCaoDoanhNghiep.thuMuc.themConTieuDe' : 'baoCaoDoanhNghiep.thuMuc.themTieuDe')}
      width={480}
      destroyOnHidden
      footer={
        <div className="space-y-3">
          {loiChung && <LoiTrongHop>{loiChung}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={dang.isPending}>
              {dang.isPending
                ? t('chung.dangLuu')
                : t(thuMuc ? 'baoCaoDoanhNghiep.thuMuc.doiTen' : 'baoCaoDoanhNghiep.thuMuc.taoThuMuc')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-3 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          gui();
        }}
      >
        {tenCha && <p className="text-xs text-content-muted">{t('baoCaoDoanhNghiep.thuMuc.taoTrong', { ten: tenCha })}</p>}
        <Field label={t('baoCaoDoanhNghiep.thuMuc.ten')} required error={loiTen}>
          {(p) => (
            <Input
              {...p}
              autoFocus
              maxLength={120}
              value={ten}
              placeholder={t('baoCaoDoanhNghiep.thuMuc.tenGoiY')}
              onChange={(e) => {
                setTen(e.target.value);
                dang.reset();
              }}
            />
          )}
        </Field>
      </form>
    </Dialog>
  );
}

/** Chuyển thư mục sang cha khác; ô chọn đã bỏ chính nó và con cháu (chu trình). */
function HopChuyenThuMuc({ thuMuc, dsThuMuc, onClose }: { thuMuc: Folder; dsThuMuc: readonly Folder[]; onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoiBaoCao();
  const chuyen = useChuyenThuMuc();
  const [cha, setCha] = useState<string | null>(thuMuc.parentId);

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('baoCaoDoanhNghiep.thuMuc.chuyenTieuDe', { ten: thuMuc.name })}
      width={480}
      destroyOnHidden
      footer={
        <div className="space-y-3">
          {chuyen.error && <LoiTrongHop>{dichLoi(chuyen.error)}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button
              variant="primary"
              size="md"
              loading={chuyen.isPending}
              onClick={() => {
                if (cha === thuMuc.parentId) {
                  onClose();
                  return;
                }
                chuyen.mutate(
                  { id: thuMuc.id, parentId: cha },
                  {
                    onSuccess: () => {
                      toast.success(t('baoCaoDoanhNghiep.thuMuc.daChuyen', { ten: thuMuc.name }));
                      onClose();
                    },
                  },
                );
              }}
            >
              {t('baoCaoDoanhNghiep.thuMuc.chuyenNut')}
            </Button>
          </div>
        </div>
      }
    >
      <div className="pt-2">
        <Field label={t('baoCaoDoanhNghiep.thuMuc.chuyenVao')}>
          {(p) => (
            <ChonThuMuc
              {...p}
              danhSach={dsThuMuc}
              giaTri={cha}
              boNhanh={thuMuc.id}
              onChon={(id) => {
                setCha(id);
                chuyen.reset();
              }}
              nhanGoc={t('baoCaoDoanhNghiep.thuMuc.ngoaiCung')}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
